import { hasStatus } from '@caresend/utils';
import cloneDeep from 'lodash.clonedeep';
import { defineComponent, ref, computed, onMounted } from 'vue';
import Check from '../../../assets/icons/check.svg.js';
import __vue_component__ from '../../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__$2 from '../../transitions/TransitionFadeOutIn/TransitionFadeOutIn.vue.js';
import __vue_component__$1 from '../../transitions/TransitionTranslate/TransitionTranslate.vue.js';
import { InProgressDisplayColor, ProgressStepsStyle, ProgressStepStatus } from './model.js';

var BaseColor;
(function (BaseColor) {
    BaseColor["GREY"] = "#D8DAE5";
    BaseColor["PRIMARY"] = "#5083EA";
    BaseColor["GREEN"] = "#12B76A";
    BaseColor["BLACK"] = "#000000";
    BaseColor["WHITE"] = "#FFFFFF";
    BaseColor["ORANGE"] = "#FDB022";
})(BaseColor || (BaseColor = {}));
var script = defineComponent({
    name: 'ProgressSteps',
    components: {
        ButtonComponent: __vue_component__,
        TransitionTranslate: __vue_component__$1,
        TransitionFadeOutIn: __vue_component__$2,
        CheckIcon: Check,
    },
    props: {
        steps: {
            type: Array,
            default: () => [],
        },
        hideIndices: {
            type: Boolean,
            default: false,
        },
        horizontallyCentered: {
            type: Boolean,
            default: false,
        },
        inProgressDisplayColor: {
            type: String,
            default: InProgressDisplayColor.DEFAULT,
        },
        stepsStyle: {
            type: String,
            default: ProgressStepsStyle.PRIMARY,
        },
        horizontal: {
            type: Boolean,
            default: true,
        },
    },
    setup(props) {
        var _a, _b;
        const stepRefs = ref([]);
        const stepContainer = ref(null);
        const stepContainerWidth = ref((_b = (_a = stepContainer.value) === null || _a === void 0 ? void 0 : _a.clientWidth) !== null && _b !== void 0 ? _b : 0);
        const stepTops = computed(() => { var _a; return (_a = stepRefs.value) === null || _a === void 0 ? void 0 : _a.map((step) => step.offsetTop); });
        const stepContentHeights = computed(() => {
            var _a;
            return (_a = stepRefs.value) === null || _a === void 0 ? void 0 : _a.map((step) => { var _a, _b; return (_b = (_a = step.children[0]) === null || _a === void 0 ? void 0 : _a.clientHeight) !== null && _b !== void 0 ? _b : step.clientHeight; });
        });
        /** Includes any margins */
        const stepHeights = computed(() => {
            var _a;
            return (_a = stepRefs.value) === null || _a === void 0 ? void 0 : _a.map((step, i) => {
                const nextStepTop = stepTops.value[i + 1] || null;
                if (!nextStepTop)
                    return step.clientHeight;
                return nextStepTop - step.offsetTop;
            });
        });
        const stepLefts = computed(() => { var _a; return (_a = stepRefs.value) === null || _a === void 0 ? void 0 : _a.map((step) => step.offsetLeft); });
        const stepContentWidths = computed(() => {
            var _a;
            return (_a = stepRefs.value) === null || _a === void 0 ? void 0 : _a.map((step) => { var _a, _b; return (_b = (_a = step.children[0]) === null || _a === void 0 ? void 0 : _a.clientWidth) !== null && _b !== void 0 ? _b : step.clientWidth; });
        });
        /** Includes any margins */
        const stepWidths = computed(() => {
            var _a;
            return (_a = stepRefs.value) === null || _a === void 0 ? void 0 : _a.map((step, i) => {
                const nextStepLeft = stepLefts.value[i + 1] || null;
                if (!nextStepLeft)
                    return step.clientWidth;
                return nextStepLeft - step.offsetLeft;
            });
        });
        const fullContentWidth = computed(() => { var _a; return (_a = stepWidths.value) === null || _a === void 0 ? void 0 : _a.reduce((acc, width) => acc + width, 0); });
        const getIsSlotEmpty = (index) => {
            var _a;
            const elementReference = stepRefs.value[index];
            const children = Object.values((_a = elementReference === null || elementReference === void 0 ? void 0 : elementReference.children) !== null && _a !== void 0 ? _a : {})
                .map((child) => child.className)
                .filter((className) => !className.includes('ProgressSteps__slotless-display'));
            return !children.length;
        };
        const xOverflow = computed(() => fullContentWidth.value > stepContainerWidth.value);
        const resizeTrigger = ref(0);
        onMounted(() => {
            const resize = () => {
                var _a;
                resizeTrigger.value += 1;
                if ((_a = stepContainer.value) === null || _a === void 0 ? void 0 : _a.clientWidth) {
                    stepContainerWidth.value = stepContainer.value.clientWidth;
                }
            };
            resize();
            window.addEventListener('resize', resize);
        });
        const displayColor = computed(() => {
            switch (props.stepsStyle) {
                case ProgressStepsStyle.PRIMARY:
                    return BaseColor.PRIMARY;
                case ProgressStepsStyle.GREEN:
                    return BaseColor.GREEN;
                default:
                    return BaseColor.GREEN;
            }
        });
        const getStepIndicatorContainerStyles = (index) => {
            var _a, _b, _c, _d;
            const contentHeight = (_a = stepContentHeights.value) === null || _a === void 0 ? void 0 : _a[index];
            const fullHeight = (_b = stepHeights.value) === null || _b === void 0 ? void 0 : _b[index];
            const marginBottom = (fullHeight !== null && fullHeight !== void 0 ? fullHeight : 0) - (contentHeight !== null && contentHeight !== void 0 ? contentHeight : 0);
            const contentWidth = (_c = stepContentWidths.value) === null || _c === void 0 ? void 0 : _c[index];
            const fullWidth = (_d = stepWidths.value) === null || _d === void 0 ? void 0 : _d[index];
            const marginRight = (fullWidth !== null && fullWidth !== void 0 ? fullWidth : 0) - (contentWidth !== null && contentWidth !== void 0 ? contentWidth : 0);
            if (fullHeight === undefined
                || fullWidth === undefined)
                return {};
            if (props.horizontal) {
                return {
                    width: `${fullWidth - marginRight}px`,
                    marginRight: `${marginRight}px`,
                };
            }
            return {
                height: `${fullHeight - marginBottom}px`,
                marginBottom: `${marginBottom}px`,
            };
        };
        const getStepIndicatorStyles = (index) => {
            const step = props.steps[index];
            const stepStatus = step === null || step === void 0 ? void 0 : step.status;
            if (stepStatus === ProgressStepStatus.INCOMPLETE)
                return null;
            let color = BaseColor.GREY;
            let textColor = BaseColor.BLACK;
            let fontWeight = '400';
            if (stepStatus === ProgressStepStatus.COMPLETE
                || stepStatus === ProgressStepStatus.IN_PROGRESS) {
                color = displayColor.value;
                textColor = BaseColor.WHITE;
                fontWeight = '600';
            }
            if (stepStatus === ProgressStepStatus.IN_PROGRESS) {
                if (props.inProgressDisplayColor === InProgressDisplayColor.ORANGE) {
                    color = BaseColor.ORANGE;
                }
            }
            return {
                backgroundColor: color,
                color: textColor,
                fontWeight,
            };
        };
        const getStepIndicatorCenter = (index) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            const stepLeft = (_b = (_a = stepLefts.value) === null || _a === void 0 ? void 0 : _a[index]) !== null && _b !== void 0 ? _b : 0;
            const stepWidth = (_d = (_c = stepContentWidths.value) === null || _c === void 0 ? void 0 : _c[index]) !== null && _d !== void 0 ? _d : 0;
            const stepIndicatorCenterX = stepLeft + (stepWidth / 2);
            const stepTop = (_f = (_e = stepTops.value) === null || _e === void 0 ? void 0 : _e[index]) !== null && _f !== void 0 ? _f : 0;
            const stepHeight = (_h = (_g = stepContentHeights.value) === null || _g === void 0 ? void 0 : _g[index]) !== null && _h !== void 0 ? _h : 0;
            const stepIndicatorCenterY = stepTop + (stepHeight / 2);
            return {
                left: stepIndicatorCenterX,
                top: stepIndicatorCenterY,
            };
        };
        const getCurrentStepIndex = () => {
            const steps = cloneDeep(props.steps).reverse();
            let latestStepIndexInverse = steps
                .findIndex((step) => hasStatus(step.status, ProgressStepStatus.IN_PROGRESS));
            if (latestStepIndexInverse < 0) {
                latestStepIndexInverse = steps.findIndex((step) => hasStatus(step.status, ProgressStepStatus.COMPLETE));
            }
            return steps.length - latestStepIndexInverse - 1;
        };
        const progressStepContainerOffsetX = computed(() => {
            if (!props.horizontal)
                return 0;
            const steps = cloneDeep(props.steps).reverse();
            let latestStepIndexInverse = steps
                .findIndex((step) => hasStatus(step.status, ProgressStepStatus.IN_PROGRESS));
            if (latestStepIndexInverse < 0) {
                latestStepIndexInverse = steps.findIndex((step) => hasStatus(step.status, ProgressStepStatus.COMPLETE));
            }
            const latestStepIndex = steps.length - latestStepIndexInverse - 1;
            const { left: latestStepIndicatorCenter } = getStepIndicatorCenter(latestStepIndex);
            if (!xOverflow.value) {
                if (props.horizontallyCentered) {
                    return (stepContainerWidth.value - fullContentWidth.value) / 2;
                }
                return 0;
            }
            if (latestStepIndicatorCenter < 0.6 * stepContainerWidth.value) {
                return 16;
            }
            const offsetRatio = 0.5;
            const offsetX = (stepContainerWidth.value * offsetRatio) - latestStepIndicatorCenter;
            return offsetX;
        });
        const getStepIndicatorLineStyles = (index) => {
            var _a, _b, _c, _d;
            if (index === 0)
                return {};
            if (props.horizontal) {
                const { left: lastStepIndicatorCenter } = getStepIndicatorCenter(index - 1);
                const { left: currentStepIndicatorCenter } = getStepIndicatorCenter(index);
                const currentStepLeft = (_b = (_a = stepLefts.value) === null || _a === void 0 ? void 0 : _a[index]) !== null && _b !== void 0 ? _b : 0;
                const offsetX = lastStepIndicatorCenter - currentStepLeft;
                const width = currentStepIndicatorCenter - lastStepIndicatorCenter;
                return {
                    left: `${offsetX}px`,
                    width: `${width}px`,
                };
            }
            const { top: lastStepIndicatorCenter } = getStepIndicatorCenter(index - 1);
            const { top: currentStepIndicatorCenter } = getStepIndicatorCenter(index);
            const currentStepTop = (_d = (_c = stepTops.value) === null || _c === void 0 ? void 0 : _c[index]) !== null && _d !== void 0 ? _d : 0;
            const offsetY = lastStepIndicatorCenter - currentStepTop;
            const height = currentStepIndicatorCenter - lastStepIndicatorCenter;
            return {
                top: `${offsetY}px`,
                height: `${height}px`,
            };
        };
        const getStepIndicatorLineInnerStyles = (index) => {
            const step = props.steps[index];
            const stepStatus = step === null || step === void 0 ? void 0 : step.status;
            if (stepStatus === ProgressStepStatus.INCOMPLETE)
                return null;
            let color = BaseColor.GREY;
            if (stepStatus === ProgressStepStatus.COMPLETE
                || stepStatus === ProgressStepStatus.IN_PROGRESS) {
                color = displayColor.value;
            }
            return {
                backgroundColor: color,
            };
        };
        const progressStepsDisplayContentClass = computed(() => {
            const classNames = [''];
            if (props.horizontal)
                classNames.push('ProgressSteps__display-content--horizontal');
            return classNames;
        });
        const progressStepContainerClass = computed(() => {
            const classNames = [''];
            if (props.horizontal)
                classNames.push('ProgressSteps--horizontal');
            return classNames;
        });
        const progressStepIndicatorsClass = computed(() => {
            const classNames = [''];
            if (props.horizontal)
                classNames.push('ProgressSteps__indicators--horizontal');
            return classNames;
        });
        const getIsSlotlessDisplayDisabled = (index) => {
            var _a;
            const step = props.steps[index];
            if ((step === null || step === void 0 ? void 0 : step.to) && getCurrentStepIndex() > index)
                return (_a = step === null || step === void 0 ? void 0 : step.navigationToDisabled) !== null && _a !== void 0 ? _a : false;
            return true;
        };
        const getSlotlessDisplayClass = (index) => {
            const step = props.steps[index];
            const classNames = [];
            if (step === null || step === void 0 ? void 0 : step.status) {
                classNames.push(`ProgressSteps__slotless-display--${step.status}`);
            }
            if (props.horizontal) {
                classNames.push('ProgressSteps__slotless-display--horizontal');
                classNames.push('mr-6');
            }
            else {
                classNames.push('mb-5');
            }
            if (!getIsSlotlessDisplayDisabled(index)) {
                classNames.push('ProgressSteps__slotless-display--link');
            }
            return classNames;
        };
        return {
            ProgressStepStatus,
            getIsSlotEmpty,
            getSlotlessDisplayClass,
            getStepIndicatorContainerStyles,
            getStepIndicatorLineInnerStyles,
            getStepIndicatorLineStyles,
            getStepIndicatorStyles,
            getIsSlotlessDisplayDisabled,
            hasStatus,
            progressStepContainerClass,
            progressStepContainerOffsetX,
            progressStepIndicatorsClass,
            progressStepsDisplayContentClass,
            resizeTrigger,
            stepContainer,
            stepRefs,
        };
    },
});

export { script as default };
